<template>
  <div ref="rootEl">
    <background-image
      :is-background="isBackground"
      :lazy-load="lazyLoad"
      :position="backgroundPosition"
      :image="adaptiveImage"
      :showImageRedius="showImageRedius"
      v-if="field?.fields && shouldShow"
    >
      <slot v-if="$slots.default" />
    </background-image>
  </div>
</template>

<script>
/**
 * @typedef AdaptiveImage
 * @extends Component
 * @property {Boolean} isBackground - Rendered as Background Image
 * @property {{
 *   fields: AdaptiveImageFields
 * }} field
 * */
/**
 * @typedef AdaptiveImageFields
 * @property {ImageField} mobileImage
 * @property {ImageField} tabletImage
 * @property {ImageField} desktopImage
 * */
import { reactive, toRefs, computed, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import _ from 'lodash';
import config from '@/temp/config';

export default {
  name: 'AdaptiveImage',
  props: {
    isBackground: {
      type: Boolean,
      default: true,
    },
    backgroundPosition: {
      type: String,
      default: 'center',
    },
    lazyLoad: {
      type: Boolean,
      default: true,
    },
    field: {
      type: Object,
    },
    showImageRedius: {
      type: Boolean,
      default: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    // 是否启用自动滚动监听
    enableScrollDetection: {
      type: Boolean,
      default: true,
    },
    // 滚动触发的偏移量
    scrollOffset: {
      type: Number,
      default: 150,
    },
  },
  setup(props) {
    const { proxy } = getCurrentInstance();

    const state = reactive({
      rootEl: null,
      internalShow: false,
    });

    const adaptiveImage = computed(() => {
      if (!props.field?.fields) {
        return null;
      }

      const result = proxy.$adaptiveImageField(
        props.field?.fields?.desktopImage,
        props.field?.fields?.mobileImage,
        props.field?.fields?.tabletImage
      );

      return result;
    });

    const shouldShow = computed(() => {
      if (props.enableScrollDetection) {
        return state.internalShow;
      }
      return props.show;
    });

    // 滚动监听
    const handleScroll = () => {
      if (!state.rootEl || !props.enableScrollDetection) return;
      const rect = state.rootEl.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.top <= windowHeight + props.scrollOffset) {
        if (!state.internalShow) {
          state.internalShow = true;
        }
      }
    };
    const throttledHandleScroll = _.throttle(handleScroll, 100);

    onMounted(() => {
      // 初始可见性检测
      if (props.enableScrollDetection && state.rootEl) {
        const rect = state.rootEl.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        if (rect.top <= windowHeight + props.scrollOffset) {
          state.internalShow = true;
        }
        window.addEventListener('scroll', throttledHandleScroll);
      }
    });

    onBeforeUnmount(() => {
      if (props.enableScrollDetection) {
        window.removeEventListener('scroll', throttledHandleScroll);
        throttledHandleScroll.cancel && throttledHandleScroll.cancel();
      }
    });

    return {
      ...toRefs(state),
      adaptiveImage,
      shouldShow,
    };
  },
};
</script>
